import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, BarChart, Bar, Cell, ReferenceLine,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { supabase } from '../supabase';
import { formatDateDisplay } from '../../utils/date';
import { Card } from '../../components/ui/card';
import {
  summarizeCollection,
  collectionStatus,
  classifyLeadTime,
  isInLateSurgeWindow,
  isSprintLagging,
  SEGMENT_META,
  summarizeHouseholds,
  sortHouseholds,
  isEnrolledSegment,
  type MonthlyCollectionRow,
  type EnrollmentProgressRow,
  type EnrollmentVelocityDetailRow,
  type EnrollmentCurvePoint,
  type FunnelStepRow,
  type FamilySegment,
  type HouseholdRow,
} from '../../utils/insights';
import { BRAND_PROFILES } from '../../config/brandProfiles';
import { AdminBrandTag } from '../components/AdminBrandTag';
import { BrandFilterSelect } from '../components/BrandFilterSelect';
import { groupByBrand, matchesBrandFilter, type BrandFilter } from '../utils/brandFilter';
import { useProgramBrands } from '../utils/useProgramBrands';

// ─── Local types ─────────────────────────────────────────────────────────────

interface PaymentTimingRow {
  day_offset: number;
  payment_count: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function monthLabel(d: Date): string {
  return d.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'America/Los_Angeles' });
}

function toMonthParam(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

function prevMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1);
}

function nextMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 1);
}


function mergeSparkline(
  current: EnrollmentCurvePoint[],
  prior: EnrollmentCurvePoint[],
  forecast?: Array<{ day: number; count: number }>,
): Array<{ day: number; current?: number; prior?: number; forecast?: number }> {
  const allDays = new Set([
    ...current.map(p => p.day),
    ...prior.map(p => p.day),
    ...(forecast ?? []).map(p => p.day),
  ]);
  const days = [...allDays].sort((a, b) => a - b);
  const cMap = new Map(current.map(p => [p.day, p.count]));
  const pMap = new Map(prior.map(p => [p.day, p.count]));
  const fMap = new Map((forecast ?? []).map(p => [p.day, p.count]));
  return days.map(day => ({ day, current: cMap.get(day), prior: pMap.get(day), forecast: fMap.get(day) }));
}

const PROFILE_COLORS: Record<string, string> = {
  sprint:   'bg-orange-100 text-orange-800',
  medium:   'bg-blue-100 text-blue-800',
  marathon: 'bg-purple-100 text-purple-800',
};

// ─── Collection Payment Timing Modal ─────────────────────────────────────────

function CollectionPaymentModal({
  programId,
  programName,
  month,
  onClose,
}: {
  programId: string;
  programName: string;
  month: string;
  onClose: () => void;
}) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['insightPaymentTiming', programId, month],
    queryFn: async (): Promise<PaymentTimingRow[]> => {
      const { data, error } = await supabase.rpc('get_insight_payment_timing_histogram', {
        p_program_id: programId,
        target_month: month,
      });
      if (error) throw error;
      return (data ?? []) as PaymentTimingRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const total = rows.reduce((s, r) => s + Number(r.payment_count), 0);
  const onTime = rows.filter(r => r.day_offset <= 0).reduce((s, r) => s + Number(r.payment_count), 0);
  const late   = total - onTime;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col justify-end sm:items-center sm:justify-center bg-black bg-opacity-40"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
      ref={el => el?.focus()}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-lg max-h-[85vh] sm:max-h-[75vh] sm:mx-4 flex flex-col">
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-b flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold">Payment Timing</h2>
            <p className="text-xs text-gray-500 mt-0.5">{programName} · days relative to due date</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1">✕</button>
        </div>

        <div className="overflow-y-auto p-4 flex flex-col gap-4">
          {isLoading ? (
            <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">No completed payments this month.</div>
          ) : (
            <>
              {/* Summary row */}
              <div className="flex gap-4 text-sm">
                <div>
                  <span className="font-semibold text-green-700">{onTime}</span>
                  <span className="text-gray-500 ml-1">on time / early ({total > 0 ? Math.round(onTime / total * 100) : 0}%)</span>
                </div>
                {late > 0 && (
                  <div>
                    <span className="font-semibold text-red-600">{late}</span>
                    <span className="text-gray-500 ml-1">late ({Math.round(late / total * 100)}%)</span>
                  </div>
                )}
              </div>

              {/* Histogram */}
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={rows} margin={{ top: 4, right: 4, bottom: 4, left: -16 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke="#f3f4f6" vertical={false} />
                    <XAxis
                      dataKey="day_offset"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      tickFormatter={v => v === 0 ? 'T0' : v > 0 ? `+${v}` : `${v}`}
                      tick={{ fontSize: 10 }}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                      labelFormatter={v => v === 0 ? 'Due date (T0)' : v > 0 ? `${v}d late` : `${Math.abs(Number(v))}d early`}
                      formatter={(val: number) => [val, 'payments']}
                    />
                    <ReferenceLine x={0} stroke="#6b7280" strokeDasharray="3 3" />
                    <Bar dataKey="payment_count" radius={[3, 3, 0, 0]}>
                      {rows.map(r => (
                        <Cell
                          key={r.day_offset}
                          fill={r.day_offset <= 0 ? '#22c55e' : r.day_offset <= 7 ? '#f59e0b' : '#ef4444'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Legend */}
              <div className="flex gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />Early / on time</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" />1–7d late</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-500 inline-block" />&gt;7d late</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Collection Progress Panel ────────────────────────────────────────────────

function CollectionProgressPanel() {
  const [month, setMonth] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [monthInitialized, setMonthInitialized] = useState(false);
  const [timingProgram, setTimingProgram] = useState<{ id: string; name: string } | null>(null);

  const { data: latestMonth } = useQuery({
    queryKey: ['insightCollectionLatestMonth'],
    queryFn: async (): Promise<Date | null> => {
      const { data, error } = await supabase.rpc('get_insight_collection_latest_month');
      if (error) throw error;
      if (!data) return null;
      const d = new Date(data as string);
      return new Date(d.getUTCFullYear(), d.getUTCMonth(), 1);
    },
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (latestMonth && !monthInitialized) {
      setMonth(latestMonth);
      setMonthInitialized(true);
    }
  }, [latestMonth, monthInitialized]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['insightMonthlyCollection', toMonthParam(month)],
    queryFn: async (): Promise<MonthlyCollectionRow[]> => {
      const { data, error } = await supabase.rpc('get_insight_monthly_collection', {
        target_month: toMonthParam(month),
      });
      if (error) throw error;
      return (data ?? []) as MonthlyCollectionRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // The RPC returns program ids only; brand comes from a lookup beside it. Totals are then
  // summed per brand and never across brands: one brand's collection rate is a figure someone
  // acts on, a blend of two is not.
  const { data: brandMap, isError: brandLookupFailed } = useProgramBrands(rows.map(r => r.program_id));
  const summaries: { label: string; summary: ReturnType<typeof summarizeCollection> }[] = brandMap
    ? groupByBrand(rows, r => brandMap.get(r.program_id)).map(g => ({
        label: BRAND_PROFILES[g.brand].shortName,
        summary: summarizeCollection(g.rows),
      }))
    : brandLookupFailed
      ? [{ label: 'All brands', summary: summarizeCollection(rows) }]
      : [];

  return (
    <Card className="p-4">
      {/* Header + month nav */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Monthly Collection Progress</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonth(prevMonth(month))}
            className="p-1 rounded hover:bg-gray-100 text-gray-600"
            aria-label="Previous month"
          >
            ←
          </button>
          <span className="text-sm font-medium w-36 text-center">{monthLabel(month)}</span>
          <button
            onClick={() => setMonth(nextMonth(month))}
            disabled={latestMonth ? month >= latestMonth : month >= nextMonth(new Date())}
            className="p-1 rounded hover:bg-gray-100 text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next month"
          >
            →
          </button>
        </div>
      </div>

      {/* Summary KPIs, one block per brand */}
      {!isLoading && rows.length > 0 && summaries.map(({ label, summary }) => (
        <div key={label} className="mb-4 p-3 bg-gray-50 rounded-lg" data-testid="collection-summary">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">{label}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <div className="text-xs text-gray-500">Total Invoiced</div>
            <div className="text-lg font-bold">${fmt(summary.total_invoiced)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Collected</div>
            <div className="text-lg font-bold text-green-700">
              ${fmt(summary.total_paid)}
              <span className="text-sm font-normal text-gray-500 ml-1">({summary.pct_collected}%)</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Processing</div>
            <div className="text-lg font-bold text-amber-600">
              ${fmt(summary.total_processing)}
              <span className="text-sm font-normal text-gray-500 ml-1">({summary.pct_processing}%)</span>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Outstanding</div>
            <div className="text-lg font-bold text-red-600">${fmt(summary.total_outstanding)}</div>
          </div>
        </div>
        </div>
      ))}

      {isLoading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No recurring invoices for this month.</div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const status = collectionStatus(row);
            const paidW   = row.total_invoiced > 0 ? (row.amount_paid / row.total_invoiced) * 100 : 0;
            const procW   = row.total_invoiced > 0 ? (row.amount_processing / row.total_invoiced) * 100 : 0;
            const remW    = Math.max(0, 100 - paidW - procW);

            return (
              <button
                key={row.program_id}
                className="w-full text-left space-y-1 hover:bg-gray-50 rounded-lg p-1.5 -mx-1.5 transition-colors"
                onClick={() => setTimingProgram({ id: row.program_id, name: row.program_name })}
                title="Click to view payment timing"
              >
                {/* Program name — full row so it never truncates */}
                <div className="font-medium text-sm">{row.program_name} <AdminBrandTag brand={brandMap?.get(row.program_id)} /></div>
                {/* Badge + stats on second row */}
                <div className="flex items-center gap-2 flex-wrap text-xs text-gray-500">
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded-full font-medium ${
                    status === 'on-track' ? 'bg-green-100 text-green-700' :
                    status === 'partial'  ? 'bg-amber-100 text-amber-700' :
                                           'bg-red-100 text-red-700'
                  }`}>
                    {status === 'on-track' ? '✓ On track' : status === 'partial' ? '~ Partial' : '⚠ At risk'}
                  </span>
                  <span>{row.invoices_covered}/{row.invoice_count} paid · ${fmt(row.amount_outstanding - row.amount_processing)} outstanding</span>
                </div>

                {/* Stacked progress bar */}
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
                  <div className="h-full bg-green-500 transition-all" style={{ width: `${paidW}%` }} title={`Paid: $${fmt(row.amount_paid)}`} />
                  <div className="h-full bg-amber-400 transition-all" style={{ width: `${procW}%` }} title={`Processing: $${fmt(row.amount_processing)}`} />
                  <div className="h-full bg-red-300 transition-all" style={{ width: `${remW}%` }} title={`Outstanding: $${fmt(row.amount_outstanding - row.amount_processing)}`} />
                </div>

                <div className="flex gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{row.pct_collected}% paid</span>
                  {row.amount_processing > 0 && (
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{row.pct_processing}% processing</span>
                  )}
                  {row.invoices_overdue > 0 && (
                    <span className="text-red-500">{row.invoices_overdue} overdue</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {timingProgram && (
        <CollectionPaymentModal
          programId={timingProgram.id}
          programName={timingProgram.name}
          month={toMonthParam(month)}
          onClose={() => setTimingProgram(null)}
        />
      )}
    </Card>
  );
}

// ─── Enrollment Detail Modal ──────────────────────────────────────────────────

function EnrollmentDetailModal({
  programId,
  programName,
  onClose,
}: {
  programId: string;
  programName: string;
  onClose: () => void;
}) {
  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['insightEnrollmentDetail', programId],
    queryFn: async (): Promise<EnrollmentVelocityDetailRow[]> => {
      const { data, error } = await supabase.rpc('get_insight_enrollment_velocity_detail', {
        p_program_id: programId,
      });
      if (error) throw error;
      return (data ?? []) as EnrollmentVelocityDetailRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const members    = rows.filter(r => r.is_bhcs_member).length;
  const nonMembers = rows.filter(r => !r.is_bhcs_member).length;
  const total      = rows.length;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col justify-end sm:items-center sm:justify-center bg-black bg-opacity-40"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      tabIndex={-1}
      ref={el => el?.focus()}
    >
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-[85vh] sm:max-h-[80vh] sm:mx-4 flex flex-col">
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h2 className="text-base font-semibold">Enrollment Detail</h2>
            <p className="text-xs text-gray-500 mt-0.5">{programName}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>

        {/* BHCS vs non-BHCS summary */}
        {!isLoading && total > 0 && (
          <div className="px-5 py-3 border-b bg-gray-50 flex gap-6 text-sm">
            <span>
              <span className="font-semibold text-blue-700">{members}</span>
              <span className="text-gray-500 ml-1">BHCS members ({total > 0 ? Math.round(members / total * 100) : 0}%)</span>
            </span>
            <span>
              <span className="font-semibold text-gray-700">{nonMembers}</span>
              <span className="text-gray-500 ml-1">Non-members ({total > 0 ? Math.round(nonMembers / total * 100) : 0}%)</span>
            </span>
            {/* Mini stacked bar */}
            <div className="flex-1 flex items-center">
              <div className="w-full h-2 rounded-full overflow-hidden bg-gray-200 flex">
                <div className="h-full bg-blue-500" style={{ width: `${total > 0 ? members / total * 100 : 0}%` }} />
                <div className="h-full bg-gray-400" style={{ width: `${total > 0 ? nonMembers / total * 100 : 0}%` }} />
              </div>
            </div>
          </div>
        )}

        <div className="overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-sm text-gray-400 py-6 text-center">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">No enrollments yet.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b">
                  <th className="pb-2 text-left font-medium">Day</th>
                  <th className="pb-2 text-left font-medium">Date</th>
                  <th className="pb-2 text-left font-medium">Student</th>
                  <th className="pb-2 text-left font-medium">Parent</th>
                  <th className="pb-2 text-left font-medium">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="py-1.5 pr-3 text-gray-500 font-mono text-xs">T+{row.enroll_day}</td>
                    <td className="py-1.5 pr-3 text-gray-600 whitespace-nowrap">{row.enrolled_at}</td>
                    <td className="py-1.5 pr-3 font-medium">{row.student_name}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{row.parent_name}</td>
                    <td className="py-1.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-medium ${
                        row.is_bhcs_member ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {row.is_bhcs_member ? 'Member' : 'Non-member'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Enrollment Progress Panel ────────────────────────────────────────────────

function EnrollmentProgressPanel() {
  const [detailProgramId, setDetailProgramId]     = useState<string>('');
  const [detailProgramName, setDetailProgramName] = useState<string>('');
  const [selectedType, setSelectedType]           = useState<string>('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['insightEnrollmentProgress'],
    queryFn: async (): Promise<EnrollmentProgressRow[]> => {
      const { data, error } = await supabase.rpc('get_insight_enrollment_progress');
      if (error) throw error;
      return (data ?? []) as unknown as EnrollmentProgressRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const [brandFilter, setBrandFilter] = useState<BrandFilter>('all');
  const { data: brandMap } = useProgramBrands(rows.map(r => r.program_id));

  const programTypes = React.useMemo(
    () => [...new Set(rows.map(r => r.program_type).filter(Boolean))].sort(),
    [rows],
  );
  const visibleRows = rows
    .filter(r => !selectedType || r.program_type === selectedType)
    .filter(r => matchesBrandFilter(brandMap?.get(r.program_id), brandFilter));

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold">Enrollment Progress & Prediction</h2>
        <div className="flex items-center gap-2">
          <BrandFilterSelect
            value={brandFilter}
            onChange={setBrandFilter}
            className="text-xs border rounded px-2 py-1 text-gray-700 bg-white"
          />
          {programTypes.length > 1 && (
            <select
              value={selectedType}
              onChange={e => setSelectedType(e.target.value)}
              className="text-xs border rounded px-2 py-1 text-gray-700 bg-white"
            >
              <option value="">All types</option>
              {programTypes.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          )}
          <span className="text-xs text-gray-400">Active programs · last 60 days</span>
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
      ) : visibleRows.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">
          {rows.length === 0 ? 'No active programs found.' : 'No programs match the selected filters.'}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {visibleRows.map(row => <ProgramEnrollmentCard key={row.program_id} row={row} brand={brandMap?.get(row.program_id)} onViewDetail={(id, name) => { setDetailProgramId(id); setDetailProgramName(name); }} />)}
        </div>
      )}

      {detailProgramId && (
        <EnrollmentDetailModal
          programId={detailProgramId}
          programName={detailProgramName}
          onClose={() => { setDetailProgramId(''); setDetailProgramName(''); }}
        />
      )}
    </Card>
  );
}

// ─── Program Enrollment Card ──────────────────────────────────────────────────

function ProgramEnrollmentCard({
  row,
  brand,
  onViewDetail,
}: {
  row: EnrollmentProgressRow;
  /** `programs.brand_key`, looked up beside the RPC; undefined while that lookup loads. */
  brand?: string;
  onViewDetail: (id: string, name: string) => void;
}) {
  const profile    = classifyLeadTime(row.lead_time_days);
  const lateSurge  = isInLateSurgeWindow(row);
  const lagging    = isSprintLagging(row);
  const hasForecast = row.days_until_start > 0 && row.projected_final_count > 0;
  const forecast   = hasForecast
    ? [
        { day: row.t0_day, count: row.current_enrollment },
        { day: row.t0_day + row.days_until_start, count: row.projected_final_count },
      ]
    : undefined;
  // Extend enrollment_curve to today so solid line reaches the forecast junction
  const enrollmentCurveWithToday = hasForecast && !row.enrollment_curve.some(p => p.day === row.t0_day)
    ? [...row.enrollment_curve, { day: row.t0_day, count: row.current_enrollment }]
    : row.enrollment_curve;
  const sparkData  = mergeSparkline(enrollmentCurveWithToday, row.prior_term_curve, forecast);
  const hasPrior   = row.prior_term_curve.length > 0;
  const xMax       = hasForecast ? row.t0_day + row.days_until_start : ('dataMax' as const);
  const fillPct    = row.projected_final_count > 0
    ? Math.min(100, Math.round(row.current_enrollment / row.projected_final_count * 100))
    : 0;

  return (
    <div className="border rounded-lg p-3 space-y-2.5 hover:border-blue-200 transition-colors">
      {/* Header — name full row, tags second row, date compact */}
      <div className="space-y-0.5">
        <div className="font-medium text-sm">{row.program_name} <AdminBrandTag brand={brand} /></div>
        <div className="flex gap-1 flex-wrap">
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${PROFILE_COLORS[profile] ?? 'bg-gray-100 text-gray-600'}`}>
            {profile}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium capitalize">
            {row.billing_type.replace(/_/g, ' ')}
          </span>
        </div>
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <span>{row.start_date} ·</span>
          <span className="whitespace-nowrap">
            {row.days_until_start > 0
              ? `${row.days_until_start}d until start`
              : row.days_until_start === 0
              ? 'Starts today'
              : `Started ${Math.abs(row.days_until_start)}d ago`}
          </span>
        </div>
      </div>

      {/* Enrollment count + projection */}
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold">{row.current_enrollment}</span>
        <span className="text-sm text-gray-500">enrolled</span>
        {row.days_until_start > 0 && (
          <span className="ml-auto text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
            → ~{row.projected_final_count} projected
          </span>
        )}
      </div>

      {/* Progress bar: current vs projected */}
      <div className="space-y-0.5">
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all"
            style={{ width: `${fillPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400">
          <span>{fillPct}% of projection</span>
          <span>{Math.round(row.expected_pct_at_t0 * 100)}% expected at T+{row.t0_day}</span>
        </div>
      </div>

      {/* Sparkline */}
      {sparkData.length > 0 && (
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={sparkData} margin={{ top: 2, right: 4, bottom: 2, left: -20 }}>
              <CartesianGrid strokeDasharray="2 2" stroke="#f3f4f6" />
              <XAxis dataKey="day" type="number" domain={[0, xMax]} tick={{ fontSize: 9 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 9 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                formatter={(value: number, name: string) => [value, name === 'current' ? 'This term' : name === 'forecast' ? 'Projected' : 'Prior term']}
                labelFormatter={(label: number) => {
                  const [year, month, day] = row.start_date.split('-').map(Number);
                  const startMs = Date.UTC(year, month - 1, day);
                  const offsetDays = label - (row.t0_day + row.days_until_start);
                  const dateMs = startMs + offsetDays * 86400000;
                  const d = new Date(dateMs);
                  const dateStr = formatDateDisplay(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`);
                  return `Day ${label} · ${dateStr}`;
                }}
              />
              <Line type="monotone" dataKey="current" stroke="#3b82f6" strokeWidth={2} dot={false} connectNulls animationBegin={0} animationDuration={1200} />
              {hasForecast && (
                <Line type="monotone" dataKey="forecast" stroke="#3b82f6" strokeWidth={1.5} strokeDasharray="5 3" dot={false} connectNulls animationBegin={1200} animationDuration={900} />
              )}
              {hasPrior && (
                <Line type="monotone" dataKey="prior" stroke="#9ca3af" strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stats row */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
        {row.median_enroll_day > 0 && <span>Median signup: day {row.median_enroll_day}</span>}
        {row.week1_capture_pct > 0 && <span>Week-1: {row.week1_capture_pct}%</span>}
        {row.prior_term_enrollment > 0 && (
          <span>
            Prior final: {row.prior_term_enrollment}
            {row.pct_vs_prior_final !== null && (
              <span className={`ml-1 font-medium ${row.pct_vs_prior_final >= 100 ? 'text-green-600' : 'text-red-500'}`}>
                ({row.pct_vs_prior_final >= 100 ? '▲' : '▼'}{row.pct_vs_prior_final}%)
              </span>
            )}
          </span>
        )}
        {row.prior_term_at_same_t0 > 0 && row.pct_vs_prior_at_same_t0 !== null && (
          <span>At same T+{row.t0_day} last term: {row.prior_term_at_same_t0}
            <span className={`ml-1 font-medium ${row.pct_vs_prior_at_same_t0 >= 100 ? 'text-green-600' : 'text-red-500'}`}>
              ({row.pct_vs_prior_at_same_t0 >= 100 ? '▲' : '▼'}{row.pct_vs_prior_at_same_t0}%)
            </span>
          </span>
        )}
      </div>

      {/* Alerts */}
      {lateSurge && (
        <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1.5">
          ⚡ Late surge window — Marathon programs add 30–50% of enrollment in the final 14 days.
          Consider sending a reminder campaign.
        </div>
      )}
      {lagging && (
        <div className="text-xs bg-red-50 border border-red-200 text-red-800 rounded px-2 py-1.5">
          ⚠ Sprint lagging — {row.days_until_start}d to start, enrollment below 85% of projection.
        </div>
      )}

      {/* Detail link */}
      <div className="pt-0.5">
        <button
          onClick={() => onViewDetail(row.program_id, row.program_name)}
          className="text-xs text-blue-600 hover:underline"
        >
          View enrollment detail →
        </button>
      </div>
    </div>
  );
}

// ─── Enrollment Funnel Panel ──────────────────────────────────────────────────

const DAYS_OPTIONS = [7, 14, 30, 60, 90] as const;

function EnrollmentFunnelPanel() {
  const [daysBack, setDaysBack]               = useState<number>(30);
  const [selectedProgram, setSelectedProgram] = useState<string>('');

  // Programs that actually had a program_view event in the selected window
  const { data: programs = [] } = useQuery({
    queryKey: ['funnelProgramsInWindow', daysBack],
    queryFn: async (): Promise<string[]> => {
      const startTs = new Date(Date.now() - daysBack * 86_400_000).toISOString();
      const { data } = await supabase
        .from('app_events')
        .select('metadata')
        .eq('event_type', 'program_view')
        .eq('success', true)
        .gte('created_at', startTs);
      const names = (data ?? [])
        .map((r: { metadata: Record<string, unknown> | null }) => r.metadata?.program_name as string | undefined)
        .filter((n): n is string => !!n);
      return [...new Set(names)].sort();
    },
    staleTime: 5 * 60 * 1000,
  });

  // Reset selection if current program has no activity in the new window
  React.useEffect(() => {
    if (selectedProgram && programs.length > 0 && !programs.includes(selectedProgram)) {
      setSelectedProgram('');
    }
  }, [programs, selectedProgram]);

  const { data: steps = [], isLoading } = useQuery({
    queryKey: ['insightFunnel', daysBack, selectedProgram],
    queryFn: async (): Promise<FunnelStepRow[]> => {
      const endTs   = new Date().toISOString();
      const startTs = new Date(Date.now() - daysBack * 86_400_000).toISOString();
      if (selectedProgram) {
        const { data, error } = await supabase.rpc('get_insight_enrollment_funnel_by_program', {
          p_program_name: selectedProgram,
          start_ts: startTs,
          end_ts: endTs,
        });
        if (error) throw error;
        return (data ?? []) as FunnelStepRow[];
      }
      const { data, error } = await supabase.rpc('get_insight_enrollment_funnel', {
        start_ts: startTs,
        end_ts: endTs,
      });
      if (error) throw error;
      return (data ?? []) as FunnelStepRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const loginCount = steps[0]?.unique_users ?? 0;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-semibold">Enrollment Funnel</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedProgram}
            onChange={e => setSelectedProgram(e.target.value)}
            className="text-xs border rounded px-2 py-1 text-gray-700 bg-white"
          >
            <option value="">All programs</option>
            {programs.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>Last</span>
            {DAYS_OPTIONS.map(d => (
              <button
                key={d}
                onClick={() => setDaysBack(d)}
                className={`px-2 py-0.5 rounded-full border transition-colors ${
                  daysBack === d
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* The funnel counts parents moving through one shared portal, so the whole-portal view
          cannot be split by brand: a login belongs to no brand. Said, rather than left for a
          reader to assume it is BHCS. */}
      {!selectedProgram && (
        <p className="text-xs text-gray-500 mb-3">
          Counts parents across both brands — BHCS and Kaleido families sign in to the same portal. Pick a program to see one.
        </p>
      )}

      {isLoading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
      ) : steps.length === 0 || loginCount === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No parent login events in this window.</div>
      ) : (
        <div className="space-y-1.5">
          {steps.map((step, i) => {
            const widthPct = Math.max(4, Math.round((step.unique_users / loginCount) * 100));
            const barColor = widthPct >= 70 ? 'bg-blue-500' : widthPct >= 40 ? 'bg-amber-400' : 'bg-red-400';
            return (
              <div key={step.step_order} className="space-y-1">
                {/* Row 1: step name + conversion stat */}
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 font-medium">{step.step}</span>
                  <span className="text-gray-500">
                    <span className="font-semibold text-gray-800">{step.conversion_pct ?? 100}%</span>
                    {' '}of {selectedProgram ? 'views' : 'logins'}
                  </span>
                </div>
                {/* Row 2: full-width bar */}
                <div className="w-full h-7 bg-gray-100 rounded overflow-hidden">
                  <div
                    className={`h-full ${barColor} rounded transition-all flex items-center`}
                    style={{ width: `${widthPct}%` }}
                  >
                    <span className="px-2 text-xs text-white font-medium whitespace-nowrap">
                      {step.unique_users.toLocaleString()}
                    </span>
                  </div>
                </div>
                {/* Drop-off indicator */}
                {i < steps.length - 1 && steps[i + 1]?.drop_off_pct != null && Number(steps[i + 1]?.drop_off_pct) > 0 && (
                  <div className="text-xs text-red-400 pl-1">
                    ↓ {steps[i + 1]?.drop_off_pct}% drop-off
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Family Personas ──────────────────────────────────────────────────────────

function methodLabel(method: string | null): string {
  return method ? method.replace(/_/g, ' ') : '—';
}

function families(n: number): string {
  return `${n} ${n === 1 ? 'family' : 'families'}`;
}

interface HouseholdList {
  title: string;
  description: string;
  rows: HouseholdRow[];
  lapsed: boolean;
}

function HouseholdListModal({ list, onClose }: { list: HouseholdList; onClose: () => void }) {
  const { title, description, rows, lapsed } = list;
  const [copied, setCopied] = useState(false);
  const emails = [...new Set(rows.flatMap(r => r.emails ?? []))];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(emails.join(', '));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy emails:', err);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex flex-col justify-end sm:items-center sm:justify-center bg-black bg-opacity-40"
      onKeyDown={e => { if (e.key === 'Escape') onClose(); }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      tabIndex={-1}
      ref={el => el?.focus()}
    >
      {/* Bottom sheet on mobile, centered dialog on sm+ */}
      <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full sm:max-w-4xl max-h-[85vh] sm:max-h-[80vh] sm:mx-4 flex flex-col">
        {/* Drag handle (mobile only) */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">{title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{description} · {families(rows.length)}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {emails.length > 0 && (
              <button
                onClick={() => { void handleCopy(); }}
                className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
              >
                {copied ? 'Copied' : `Copy ${emails.length} emails`}
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto p-4 space-y-2">
          {rows.length === 0 ? (
            <div className="text-sm text-gray-400 py-6 text-center">No families in this segment.</div>
          ) : (
            <>
              {/* Desktop table */}
              <table className="hidden sm:table min-w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b">
                    <th className="pb-2 text-left font-medium">Family</th>
                    {lapsed ? (
                      <>
                        <th className="pb-2 text-left font-medium">Last program</th>
                        <th className="pb-2 text-right font-medium">Ended</th>
                        <th className="pb-2 text-center font-medium">Afterschool last yr</th>
                      </>
                    ) : (
                      <>
                        <th className="pb-2 text-center font-medium">Afterschool</th>
                        <th className="pb-2 text-right font-medium">Classes</th>
                        <th className="pb-2 text-right font-medium">Camps</th>
                      </>
                    )}
                    <th className="pb-2 text-right font-medium">Billed this yr</th>
                    <th className="pb-2 text-center font-medium">Autopay</th>
                    <th className="pb-2 text-right font-medium">Past due</th>
                    <th className="pb-2 text-left font-medium pl-3">Method</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(row => (
                    <tr key={row.household_id} className="hover:bg-gray-50 align-top">
                      <td className="py-1.5 pr-3">
                        <div className="font-medium">{row.guardians}</div>
                        {row.students && <div className="text-xs text-gray-500">{row.students}</div>}
                        {row.emails && <div className="text-xs text-gray-400">{row.emails.join(', ')}</div>}
                      </td>
                      {lapsed ? (
                        <>
                          <td className="py-1.5 pr-3 text-xs text-gray-600">{row.last_program ?? '—'}</td>
                          <td className="py-1.5 pr-3 text-right text-xs text-gray-600 whitespace-nowrap">
                            {row.last_end_date ? formatDateDisplay(row.last_end_date) : '—'}
                          </td>
                          <td className="py-1.5 pr-3 text-center">
                            {row.afterschool_last_year
                              ? <span className="text-rose-600 text-xs font-medium">✓</span>
                              : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-1.5 pr-3 text-center">
                            {row.afterschool
                              ? <span className="text-green-600 text-xs font-medium">✓</span>
                              : <span className="text-gray-300 text-xs">—</span>}
                          </td>
                          <td className="py-1.5 pr-3 text-right">{row.class_enrollments || '—'}</td>
                          <td className="py-1.5 pr-3 text-right">{row.camp_enrollments || '—'}</td>
                        </>
                      )}
                      <td className="py-1.5 pr-3 text-right text-gray-700 whitespace-nowrap">
                        {Number(row.billed_this_year) > 0 ? `$${fmt(Number(row.billed_this_year))}` : '—'}
                      </td>
                      <td className="py-1.5 pr-3 text-center">
                        {row.autopay
                          ? <span className="text-green-600 text-xs font-medium">✓</span>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="py-1.5 pr-3 text-right whitespace-nowrap">
                        {Number(row.past_due_amount) > 0
                          ? <span className="text-red-600 font-medium">${fmt(Number(row.past_due_amount))}</span>
                          : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="py-1.5 pl-3 text-xs text-gray-500 capitalize">{methodLabel(row.dominant_method)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile cards */}
              <div className="sm:hidden space-y-2">
                {rows.map(row => (
                  <div key={row.household_id} className="border border-gray-100 rounded-lg p-3 space-y-1">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-sm leading-tight">{row.guardians}</span>
                      <div className="flex gap-1.5 flex-shrink-0">
                        {Number(row.past_due_amount) > 0 && (
                          <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">
                            ${fmt(Number(row.past_due_amount))} due
                          </span>
                        )}
                        {row.autopay && <span className="text-xs bg-green-50 text-green-700 px-1.5 py-0.5 rounded font-medium">Autopay</span>}
                      </div>
                    </div>
                    {row.students && <p className="text-xs text-gray-500">{row.students}</p>}
                    {row.emails && <p className="text-xs text-gray-400 truncate">{row.emails.join(', ')}</p>}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-600 pt-0.5">
                      {lapsed ? (
                        <>
                          {row.last_program && <span>{row.last_program}</span>}
                          {row.last_end_date && <span>ended {formatDateDisplay(row.last_end_date)}</span>}
                          {row.afterschool_last_year && <span className="text-rose-600">afterschool last yr</span>}
                        </>
                      ) : (
                        <>
                          {row.afterschool && <span>Afterschool</span>}
                          {row.class_enrollments > 0 && <span>{row.class_enrollments} classes</span>}
                          {row.camp_enrollments > 0 && <span>{row.camp_enrollments} camps</span>}
                        </>
                      )}
                      {Number(row.billed_this_year) > 0 && <span>${fmt(Number(row.billed_this_year))} this yr</span>}
                      {row.dominant_method && <span className="capitalize">{methodLabel(row.dominant_method)}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function FamilyPersonaPanel() {
  const [openList, setOpenList] = useState<HouseholdList | null>(null);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['insightFamilyHouseholds'],
    queryFn: async (): Promise<HouseholdRow[]> => {
      const { data, error } = await supabase.rpc('get_insight_family_households');
      if (error) throw error;
      return (data ?? []) as HouseholdRow[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { overview, segments } = useMemo(() => summarizeHouseholds(rows), [rows]);
  const renewalPct = overview.afterschoolLastYear > 0
    ? Math.round(overview.afterschoolRenewed / overview.afterschoolLastYear * 100)
    : 0;
  const autopayPct = overview.enrolled > 0 ? Math.round(overview.autopayEnrolled / overview.enrolled * 100) : 0;

  const openSegment = (segment: FamilySegment) => {
    setOpenList({
      title: segment,
      description: SEGMENT_META[segment].description,
      rows: sortHouseholds(rows.filter(r => r.segment === segment), segment),
      lapsed: segment === 'Lapsed',
    });
  };

  const openPastDue = () => {
    setOpenList({
      title: 'Past Due',
      description: 'Unpaid after the due date, with no payment clearing',
      rows: rows.filter(r => Number(r.past_due_amount) > 0)
        .sort((a, b) => Number(b.past_due_amount) - Number(a.past_due_amount)),
      lapsed: false,
    });
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Family Personas</h2>
        {overview.households > 0 && (
          <span className="text-xs text-gray-400">
            {overview.households} households · {overview.parentAccounts} parent accounts
          </span>
        )}
      </div>
      {/* Families are never brand-scoped, so a household reads its enrollments in both. */}
      <p className="text-xs text-gray-500 mb-3">
        A family is every parent linked through a shared child, across BHCS and Kaleido. Segments read
        active enrollments; registration and waitlist rows don't count. Billed covers this school year
        (from August 1), every program type.
      </p>

      {isLoading ? (
        <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-400 py-8 text-center">No active parent data found.</div>
      ) : (
        <>
          <div className="mb-4 p-3 bg-gray-50 rounded-lg grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div>
              <div className="text-xs text-gray-500">Enrolled families</div>
              <div className="text-lg font-bold">{overview.enrolled}</div>
              <div className="text-xs text-gray-400">of {overview.households} households</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Afterschool renewal</div>
              <div className="text-lg font-bold">{renewalPct}%</div>
              <div className="text-xs text-gray-400">
                {overview.afterschoolRenewed} of {overview.afterschoolLastYear} from last school year
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">New this school year</div>
              <div className="text-lg font-bold">{overview.newThisYear}</div>
              <div className="text-xs text-gray-400">enrolled families</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">Autopay</div>
              <div className="text-lg font-bold">{autopayPct}%</div>
              <div className="text-xs text-gray-400">{overview.autopayEnrolled} enrolled</div>
            </div>
            {overview.pastDueHouseholds > 0 ? (
              <button onClick={openPastDue} className="text-left hover:opacity-80">
                <div className="text-xs text-gray-500">Past due</div>
                <div className="text-lg font-bold text-red-600">${fmt(overview.pastDueAmount)}</div>
                <div className="text-xs text-red-500">{families(overview.pastDueHouseholds)} · view →</div>
              </button>
            ) : (
              <div>
                <div className="text-xs text-gray-500">Past due</div>
                <div className="text-lg font-bold text-green-700">$0.00</div>
                <div className="text-xs text-gray-400">no family past due</div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {segments.map(s => {
              const meta = SEGMENT_META[s.segment];
              const enrolled = isEnrolledSegment(s.segment);
              const sharePct = enrolled && overview.enrolled > 0
                ? Math.round(s.households / overview.enrolled * 100)
                : 0;
              return (
                <button
                  key={s.segment}
                  onClick={() => openSegment(s.segment)}
                  className={`text-left p-3.5 rounded-lg border ${meta.color} hover:opacity-90 transition-opacity space-y-2`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-semibold text-sm">{s.segment}</div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-xl font-bold leading-tight">{s.households.toLocaleString()}</div>
                      <div className="text-xs opacity-70">
                        {enrolled ? `${sharePct}% of enrolled` : `${s.parentAccounts} parent accounts`}
                      </div>
                    </div>
                  </div>
                  <div className="text-xs opacity-75 leading-tight">{meta.description}</div>
                  {enrolled ? (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div>
                        <span className="opacity-60">Avg students</span>
                        <div className="font-medium">{s.avgStudents}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Avg billed this yr</span>
                        <div className="font-medium">${fmt(s.avgBilledThisYear)}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Autopay</span>
                        <div className="font-medium">{s.pctAutopay}%</div>
                      </div>
                      <div>
                        <span className="opacity-60">Past due</span>
                        <div className={`font-medium ${s.pastDue > 0 ? 'text-red-600' : ''}`}>{families(s.pastDue)}</div>
                      </div>
                      <div>
                        <span className="opacity-60">New this yr</span>
                        <div className="font-medium">{s.newThisYear}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Primary method</span>
                        <div className="font-medium capitalize">{methodLabel(s.dominantMethod)}</div>
                      </div>
                    </div>
                  ) : s.segment === 'Lapsed' ? (
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                      <div>
                        <span className="opacity-60">Afterschool last yr</span>
                        <div className="font-medium">{s.afterschoolLastYear}</div>
                      </div>
                      <div>
                        <span className="opacity-60">Primary method</span>
                        <div className="font-medium capitalize">{methodLabel(s.dominantMethod)}</div>
                      </div>
                    </div>
                  ) : null}
                  <div className="text-xs opacity-50 text-right">{meta.hint} · view families →</div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {openList && <HouseholdListModal list={openList} onClose={() => setOpenList(null)} />}
    </Card>
  );
}

export { CollectionProgressPanel, EnrollmentProgressPanel, EnrollmentFunnelPanel, FamilyPersonaPanel };
